'use client'

import type { Variants } from 'motion/react'
import { motion } from 'motion/react'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import type { MenuItemType } from '../../frontend-types'
import { EASE_OUT_CUBIC, MENU_DURATION } from '../../helpers/timing'
import { Corridor } from '../atoms/Corridor'
import { FontAwesomeIcon } from '../atoms/FontAwesomeIcon'
import { MegaMenuItem } from './MegaMenuItem'

const MOBILE_MENU_DELAY = 0.1

export interface MegaMenuMobileProps {
	items?: MenuItemType[]
}

const menuWrapperVariants: Variants = {
	hidden: {
		opacity: 0,
		transition: {
			duration: MENU_DURATION,
			delay: MOBILE_MENU_DELAY,
			ease: EASE_OUT_CUBIC,
		},
	},
	visible: {
		opacity: 1,
		transition: { duration: MENU_DURATION, ease: EASE_OUT_CUBIC },
	},
}

const menuContentVariants: Variants = {
	hidden: {
		opacity: 0,
		x: '4rem',
		transition: {
			duration: MENU_DURATION,
			ease: EASE_OUT_CUBIC,
		},
	},
	visible: {
		opacity: 1,
		x: 0,
		transition: {
			duration: MENU_DURATION,
			delay: MOBILE_MENU_DELAY,
			ease: EASE_OUT_CUBIC,
		},
	},
}

export const MegaMenuMobile: FC<MegaMenuMobileProps> = ({ items }) => {
	const [activeIndex, setActiveIndex] = useState<number | null>(null)

	// Handle Escape key with higher priority
	useEffect(() => {
		if (activeIndex === null) return

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault()
				event.stopImmediatePropagation()
				setActiveIndex(null)
			}
		}

		// Use capture phase to intercept before other handlers
		window.addEventListener('keydown', handleKeyDown, true)
		return () => window.removeEventListener('keydown', handleKeyDown, true)
	}, [activeIndex])

	return (
		<div className="col-span-full block tablet-l:col-span-8 tablet-l:hidden">
			<nav className="flex flex-col">
				{items?.map((item, index) => (
					<button
						id={`mobile-menu-button-${index}`}
						key={item.href || index}
						className="flex cursor-pointer items-center justify-between gap-x-xl border-b-(length:--stroke-xs) border-b-blue-tint-40 py-3xl text-left"
						aria-controls={`mobile-menu-item-${index}`}
						aria-expanded={activeIndex === index}
						onClick={() => setActiveIndex(index)}
					>
						<span className="text-h3 text-white">{item.title}</span>
						<FontAwesomeIcon
							icon={['fal', 'arrow-right']}
							size="lg"
							className="text-blue-tint-40"
						/>
					</button>
				))}
			</nav>
			{items?.map((item, index) => {
				const active = index === activeIndex
				return (
					<motion.div
						key={item.href || index}
						id={`mobile-menu-item-${index}`}
						className="fixed top-0 left-0 h-full w-full bg-blue"
						role="dialog"
						aria-modal="true"
						aria-labelledby={`mobile-menu-button-${index}`}
						variants={menuWrapperVariants}
						initial="hidden"
						animate={active ? 'visible' : 'hidden'}
						inert={!active}
					>
						<motion.div
							className="absolute inset-0"
							variants={menuContentVariants}
							initial="hidden"
							animate={active ? 'visible' : 'hidden'}
						>
							<Corridor>
								<div className="mt-19 h-full w-full pt-5xl desktop:mt-27">
									<div className="mb-3xl w-full border-b-(length:--stroke-xs) border-b-blue-tint-40 pb-xl">
										<button
											className="flex w-fit cursor-pointer items-center justify-start gap-x-sm text-blue-tint-10"
											aria-label="Back to main menu"
											onClick={() => setActiveIndex(null)}
										>
											<FontAwesomeIcon icon={['fal', 'arrow-left']} size="sm" aria-hidden="true" />
											<span className="block text-h4">All</span>
										</button>
									</div>
									<MegaMenuItem megamenuItem={item} />
								</div>
							</Corridor>
						</motion.div>
					</motion.div>
				)
			})}
		</div>
	)
}
